import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import axios from "axios";
import { toast } from "react-toastify";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";

// Initialize Stripe with your publishable key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Separate the form component that uses Stripe hooks
const CreditCardFormContent = ({
  lead,
  onSelectedOption,
  prorated,
  captchaValue,
}) => {
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    if (success) {
      navigate(`/payment/${lead?._id}/success`);
    }
  }, [success, navigate, lead?._id]);

  const totalOnetimeFees = lead?.onetime_fees?.reduce(
    (acc, curr) => acc + parseFloat(curr.feeAmount),
    0
  );

  const handleSave = async (data) => {
    try {
      setIsLoading(true);

      if (!captchaValue) {
        toast.error("Please complete the captcha");
        setIsLoading(false);
        return;
      }

      if (!stripe || !elements) {
        toast.error("Stripe has not loaded. Please try again.");
        setIsLoading(false);
        return;
      }

      // Create a PaymentMethod using CardElement
      const cardElement = elements.getElement(CardElement);
      const { error: paymentMethodError, paymentMethod } =
        await stripe.createPaymentMethod({
          type: "card",
          card: cardElement,
          billing_details: {
            name: data.card_holder_name,
            email: lead?.email,
          },
        });

      if (paymentMethodError) {
        toast.error(paymentMethodError.message);
        setIsLoading(false);
        return;
      }

      // Send PaymentMethod ID to your backend
      const subscription = await axios.post(
        `${process.env.REACT_APP_API_URL}stripes/cards`,
        {
          name: data.card_holder_name,
          lead_name: `${lead?.firstname} ${lead?.lastname}`,
          email: lead?.email,
          paymentMethodId: paymentMethod.id,
          priceID: lead?.package?.price_id,
          selected_method: lead?.paymentMethod,
          one_time_fees: totalOnetimeFees,
          contract_length: lead?.contract_length,
          recurring_date: lead?.billing_date,
          amount: lead?.package?.amount,
          lead: lead?._id,
          prorated,
          captchaValue,
          manager: {
            name: `${lead?.manager?.firstname} ${lead?.manager?.lastname}`,
            email: lead?.manager?.email,
          },
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      // Handle 3D Secure if required
      if (subscription.data.requiresAction) {
        const { error: confirmError, paymentIntent } =
          await stripe.confirmCardPayment(subscription.data.clientSecret);

        if (confirmError) {
          toast.error(confirmError.message);
          setIsLoading(false);
          return;
        }

        if (paymentIntent.status === "succeeded") {
          toast.success("Payment successful!");
        }
      } else {
        toast.success("Card submitted successfully!");
      }

      // Handle client creation and customer association
      if (lead?.paymentMethod === "card") {
        const client = await axios.post(
          `${process.env.REACT_APP_API_URL}clients`,
          {
            status: "Pending agreement",
            lead: lead?._id,
            manager: lead?.manager?._id,
            initial_payment:
              Number(prorated) > 0
                ? prorated + totalOnetimeFees
                : totalOnetimeFees + lead?.package?.amount,
          }
        );

        await axios.post(`${process.env.REACT_APP_API_URL}customers`, {
          customer_id: subscription?.data?.data.id,
          client: client?.data?.data?._id,
        });
      }

      if (lead?.paymentMethod !== "card") {
        onSelectedOption();
      } else {
        setSuccess(true);
      }
    } catch (error) {
      const msg =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        "An error occurred. Please try again.";
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <form onSubmit={handleSubmit(handleSave)}>
        <div className="rounded-md w-full p-6 mx-auto lg:w-2/3">
          <div className="my-3">
            <label className="mb-3 block text-base font-medium text-[#07074D]">
              Card Holder Name
            </label>
            <input
              type="text"
              placeholder="Card Holder Name"
              {...register("card_holder_name", { required: true })}
              className="w-full rounded-md border border-[#e0e0e0] bg-white py-3 px-6 text-base font-medium text-[#6B7280] outline-none focus:border-[#003049] focus:shadow-md"
            />
            {errors.card_holder_name && (
              <span className="text-red-600">Card Holder name is required</span>
            )}
          </div>
          <div className="my-3">
            <label className="mb-3 block text-base font-medium text-[#07074D]">
              Card Details
            </label>
            <CardElement
              options={{
                style: {
                  base: {
                    fontSize: "16px",
                    color: "#6B7280",
                    "::placeholder": { color: "#aab7c4" },
                  },
                  invalid: { color: "#9e2146" },
                },
              }}
              className="w-full rounded-md border border-[#e0e0e0] bg-white py-3 px-6 text-base font-medium outline-none focus:border-[#003049] focus:shadow-md"
            />
          </div>
          <div className="flex items-center">
            <span className="text-red-500 my-4">
              NB: State and Local taxes applicable (exclusive of original
              amount)
            </span>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              value="agreed"
              className="form-checkbox h-5 w-5 text-red-600 mr-2"
              {...register("agreed", { required: true })}
            />
            <span className="text-gray-500 my-4">
              Accept terms and conditions
            </span>
          </div>
          {errors.agreed && (
            <span className="text-red-600">
              You must agree to the terms and conditions
            </span>
          )}
          {error && <span className="text-red-600">{error}</span>}
        </div>
        <div className="my-4 mx-auto lg:w-2/3 flex justify-between">
          <button
            onClick={() => navigate(-1)}
            type="button"
            className="block text-center uppercase bg-white hover:bg-white focus:shadow-outline focus:outline-none text-[#0E669B] text-xs py-3 px-10 rounded"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading || !stripe}
            className="block text-center uppercase shadow bg-[#0E669B] hover:bg-[#0E669B] focus:shadow-outline focus:outline-none text-white text-xs py-3 px-10 rounded disabled:opacity-50"
          >
            {isLoading ? "Loading..." : "Pay"}
          </button>
        </div>
      </form>
    </div>
  );
};

// Main component that wraps the form with Elements provider
const CreditCardForm = ({ lead, onSelectedOption, prorated, captchaValue }) => {
  return (
    <Elements stripe={stripePromise}>
      <CreditCardFormContent
        lead={lead}
        onSelectedOption={onSelectedOption}
        prorated={prorated}
        captchaValue={captchaValue}
      />
    </Elements>
  );
};

export default CreditCardForm;
